import { type Currency } from '@/api/schemas/catalog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

export type BillingCadence = 'monthly' | 'annual'

/** Monthly / Annual segmented toggle with the −20% badge (pricing mockup). */
export function BillingToggle({
  value,
  onChange,
}: {
  value: BillingCadence
  onChange: (next: BillingCadence) => void
}) {
  const options: { key: BillingCadence; label: string }[] = [
    { key: 'monthly', label: 'Monthly' },
    { key: 'annual', label: 'Annual' },
  ]
  return (
    <div
      role="radiogroup"
      aria-label="Billing period"
      className="flex rounded-[10px] border border-line bg-surface-sunken p-[3px]"
    >
      {options.map((option) => {
        const selected = value === option.key
        return (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.key)}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-surface-raised font-semibold text-content shadow-card'
                : 'text-content-secondary hover:text-content',
            )}
          >
            {option.label}
            {option.key === 'annual' ? (
              <span className="rounded bg-accent-soft px-1.5 py-0.5 font-mono text-[10px] font-bold text-accent-strong">
                −20%
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/** Currency picker fed by GET /api/v1/catalog/currencies. */
export function CurrencySelect({
  currencies,
  value,
  onChange,
}: {
  currencies: Currency[] | undefined
  value: string
  onChange: (code: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 rounded-[9px]" aria-label="Currency">
        <SelectValue placeholder={value} />
      </SelectTrigger>
      <SelectContent>
        {(currencies ?? []).map((currency) => (
          <SelectItem key={currency.code} value={currency.code}>
            {currency.code} {currency.symbol}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
